import * as React from 'react'
import { GetServerSidePropsContext } from 'next'
import { useAmp } from 'next/amp'
import getConfig from 'next/config'
import Head from 'next/head'

import {
  loggerProvider,
  withCacheConfiguration,
} from '@thg-commerce/enterprise-core'
import { EnterpriseNextPage } from '@thg-commerce/enterprise-core/src/App/types'
import { PageCacheConfiguration } from '@thg-commerce/enterprise-core/src/cache/configuration'
import { loadConfiguration } from '@thg-commerce/enterprise-core/src/ConfigurationLoader/ConfigurationLoader'
import { NextConfig } from '@thg-commerce/enterprise-core/src/ConfigurationLoader/types'
import { createApolloWithRequest } from '@thg-commerce/enterprise-core/src/Factory/apollo'
import { ContentHeaderLayout } from '@thg-commerce/enterprise-core/src/Layouts'
import { EnterpriseRequest } from '@thg-commerce/enterprise-core/src/Server/types'
import {
  ContentSlug,
  ContentSlugType,
  NetworkClient,
  RootQueryContentSlugArgs,
  SeoConfig,
} from '@thg-commerce/enterprise-network'
import { ContentSlug as ContentSlugResolver } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/Content/ContentSlug'
import { Seo as SeoResolver } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/Content/Seo'
import { clientConfig as clientConfigFactory } from '@thg-commerce/enterprise-network/src/ApolloProvider/utils'

import { ArticleData, ArticlePage, CategoryPage } from '../../src/components'
import {
  BlogNavigation,
  loadBlogNavigation,
} from '../../src/fetchers/blogNavigation'
import { ContentSlug as CONTENT_SLUG_QUERY } from '../../src/graphql/ContentSlug.graphql'
import { Category, Post, PostAuthor as User, Tag } from '../../src/types'
import { getInfoFromQueryParam, getPaginationVaribles } from '../../src/utils'
import CustomError from '../_error'

export const POSTS_PER_PAGE = 15

type SlugPageData = ArticleData | Category | Tag | User | null

export interface SlugPageProps {
  type: ContentSlugType | null
  data: SlugPageData
  page?: number
  hasWebP: boolean
  blogNavigation: BlogNavigation
}

const Slug: EnterpriseNextPage<SlugPageProps> = (props) => {
  const { type, data, page } = props
  const isAmp = useAmp()

  if (!type || !data) {
    return (
      <React.Fragment>
        <Head>
          <meta name="robots" content="noindex" />
        </Head>
        <CustomError statusCode={404} />
      </React.Fragment>
    )
  }

  if (type === ContentSlugType.Post) {
    const { post, content, seoConfig } = data as ArticleData
    return (
      <ArticlePage
        isAmp={isAmp}
        hasWebP={props.hasWebP}
        post={post}
        content={content}
        seoConfig={seoConfig}
      />
    )
  }
  if (Object.values(ContentSlugType).includes(type) && page) {
    return (
      <CategoryPage
        isAmp={isAmp}
        category={data as Category | Tag}
        page={page}
        hasWebP={props.hasWebP}
      />
    )
  }

  return (
    <React.Fragment>
      <Head>
        <meta name="robots" content="noindex" />
      </Head>
      <CustomError statusCode={404} />
    </React.Fragment>
  )
}

export const getServerSideProps = withCacheConfiguration<
  { props: SlugPageProps },
  GetServerSidePropsContext
>(
  async (
    context: GetServerSidePropsContext,
  ): Promise<{ props: SlugPageProps; cache?: PageCacheConfiguration }> => {
    const nextConfig = getConfig() as NextConfig
    const request = context.req as EnterpriseRequest
    const config = await loadConfiguration(nextConfig, request)
    const { publicRuntimeConfig } = config
    const {
      brand,
      subsite,
      originUrl,
      domain,
    } = publicRuntimeConfig.siteDefinition
    const logger = loggerProvider({ brand, subsite, originUrl })
    const apollo = createApolloWithRequest(request, config, true)
    const allowOverrides =
      request.headers['x-enterprise-allow-overrides'] === '1'
    const allowSitePropertyOverrides =
      request.headers['x-enterprise-allow-site-property-overrides'] === '1'
    const clientConfig = clientConfigFactory(
      publicRuntimeConfig,
      allowOverrides,
      allowSitePropertyOverrides,
    )

    apollo.setResolvers({
      Query: {
        contentSlug: ContentSlugResolver(apollo, clientConfig),
        seo: SeoResolver(apollo, clientConfig),
      },
    })

    const blogNavigation = await loadBlogNavigation(config, apollo)

    const { query, res } = context

    if (query.slug) {
      const { slug: slugToQuery, page } = getInfoFromQueryParam(query.slug)

      const categoryLimit = 0 // in tesseract 0 means no limit, currently we display all child categories regardless of amount
      const categoryOffset = 0

      const { offset: postOffset, limit: postLimit } = getPaginationVaribles(
        page || 1,
        POSTS_PER_PAGE,
      )

      const { data } = await apollo.query<
        { contentSlug: ContentSlug; seo: SeoConfig },
        RootQueryContentSlugArgs & {
          categoryLimit: number
          categoryOffset: number
          postLimit: number
          postOffset: number
        }
      >({
        query: CONTENT_SLUG_QUERY,
        variables: {
          categoryLimit,
          categoryOffset,
          postLimit,
          postOffset,
          path:
            query.slug[0] === 'tag'
              ? `${query.slug[0]}/${slugToQuery}`
              : slugToQuery,
        },
        context: { clientName: NetworkClient.Content },
      })

      if (!data || !data?.contentSlug?.type || !data?.contentSlug?.data) {
        if (res) res.statusCode = 404
        return {
          props: {
            blogNavigation,
            type: null,
            data: null,
            hasWebP: false,
          },
        }
      }

      const cacheTTLConfig: PageCacheConfiguration = {
        ttl: 3600, // 1 hour
        grace: 2629800, // 1 Month
        config: {
          domain,
        },
      }

      if (data.contentSlug.type === ContentSlugType.Post) {
        // This validation is temporary until work is complete on REBUILD-876, to prevent the need for complicated code when rendering
        const post = data?.contentSlug.data
          ? (data.contentSlug.data as Post)
          : null
        const seoConfig = data?.seo ? data.seo : null

        if (!post) {
          if (res) res.statusCode = 404
          return {
            props: {
              blogNavigation,
              type: null,
              data: null,
              hasWebP: false,
            },
          }
        }

        try {
          const content = JSON.parse(post.content)

          return {
            props: {
              blogNavigation,
              type: data.contentSlug.type,
              data: { post, content, seoConfig },
              hasWebP:
                (context?.req?.headers?.hasOwnProperty('accept') &&
                  context?.req?.headers?.accept?.includes('image/webp')) ||
                false,
            },
            cache: cacheTTLConfig,
          }
        } catch (e) {
          const error = e as Error
          if (res) res.statusCode = 404
          logger.error(
            `An error occured whilst validation article (${query.slug}): ${error.message}`,
          )
        }
      }

      if (data.contentSlug.type === ContentSlugType.User) {
        const userData: User = data.contentSlug.data as User
        userData.name = userData.username

        return {
          props: {
            blogNavigation,
            type: data.contentSlug.type,
            page: page || 1,
            data: userData,
            hasWebP:
              (context?.req?.headers?.hasOwnProperty('accept') &&
                context?.req?.headers?.accept?.includes('image/webp')) ||
              false,
          },
          cache: cacheTTLConfig,
        }
      }
      if (
        data.contentSlug.type !== ContentSlugType.Post &&
        Object.values(ContentSlugType).includes(data.contentSlug.type)
      ) {
        return {
          props: {
            blogNavigation,
            type: data.contentSlug.type,
            page: page || 1,
            data: data.contentSlug.data as Category | Tag | User,
            hasWebP:
              (context?.req?.headers?.hasOwnProperty('accept') &&
                context?.req?.headers?.accept?.includes('image/webp')) ||
              false,
          },
          cache: cacheTTLConfig,
        }
      }
    }
  },
)

Slug.Layout = ContentHeaderLayout

Slug.useAmpLayout = true

export default Slug
